/* eslint-disable i18next/no-literal-string */
import {Table} from '@dropbox/dig-components/table';
import {Text} from '@dropbox/dig-components/typography';
import cx from 'classnames';
import type {HeadcountRollup, WireframeReportSpend} from 'client';
import type {ReactNode} from 'react';

import styles from './WireframeReport.module.css';
import {
  formatHeadcount,
  formatShare,
  formatUsd,
  toNumber,
  WIREFRAME_GEOS,
} from './wireframeReportFormat';

export type HeadcountBreakdownRow = {
  key: string | number;
  name: string;
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
  companyShare: string | null;
  /** A catch-all row, such as Unassigned, that reads quieter than named rows. */
  isMuted?: boolean;
};

const BreakdownRow = ({
  name,
  headcount,
  spend,
  share,
  isBold = false,
  isMuted = false,
  className,
}: {
  name: ReactNode;
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
  share: string;
  isBold?: boolean;
  isMuted?: boolean;
  className?: string;
}) => {
  const color = isMuted ? 'subtle' : undefined;
  const cell = (value: ReactNode) => (
    <Text size="small" isBold={isBold} color={color}>
      {value}
    </Text>
  );
  return (
    <Table.Row className={className}>
      <Table.Cell className={styles.breakdownName}>{cell(name)}</Table.Cell>
      {WIREFRAME_GEOS.map((geo) => (
        <Table.Cell key={geo.key} className={styles.numeric}>
          {cell(formatHeadcount(headcount[geo.headcount]))}
        </Table.Cell>
      ))}
      <Table.Cell className={styles.numeric}>
        {cell(formatHeadcount(headcount.headcount_fte))}
      </Table.Cell>
      <Table.Cell className={styles.numeric}>{cell(share)}</Table.Cell>
      <Table.Cell className={styles.numeric}>{cell(formatUsd(spend.total_usd))}</Table.Cell>
    </Table.Row>
  );
};

/** Headcount by Cost Location, share of company, and spend per row, closed by the company total. */
export const HeadcountBreakdownTable = ({
  label,
  rows,
  total,
}: {
  /** Header of the name column, e.g. "Pillar". */
  label: string;
  rows: HeadcountBreakdownRow[];
  total: {headcount: HeadcountRollup; spend: WireframeReportSpend};
}) => (
  <div className={styles.tableScroll}>
    <Table
      isNative
      spacing="small"
      hasDividers
      className={cx(styles.numericTable, styles.breakdownTable)}
    >
      <colgroup>
        <col className={styles.breakdownNameColumn} />
        {/* Cost Locations, then HC. */}
        {[...WIREFRAME_GEOS.map((geo) => geo.key), 'headcount'].map((key) => (
          <col key={key} className={styles.breakdownFigureColumn} />
        ))}
        <col className={styles.breakdownShareColumn} />
        <col className={styles.breakdownSpendColumn} />
      </colgroup>
      <Table.Header>
        <Table.Row isHeader>
          <Table.HeaderCell>{label}</Table.HeaderCell>
          {WIREFRAME_GEOS.map((geo) => (
            <Table.HeaderCell key={geo.key} className={styles.breakdownHeader}>
              {geo.shortLabel}
            </Table.HeaderCell>
          ))}
          <Table.HeaderCell className={styles.breakdownHeader}>HC</Table.HeaderCell>
          <Table.HeaderCell className={styles.breakdownHeader}>% of company</Table.HeaderCell>
          <Table.HeaderCell className={styles.breakdownHeader}>HC $</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row) => (
          <BreakdownRow
            key={row.key}
            name={row.name}
            headcount={row.headcount}
            spend={row.spend}
            share={formatShare(row.companyShare)}
            isMuted={row.isMuted}
          />
        ))}
        <BreakdownRow
          name="Company total"
          headcount={total.headcount}
          spend={total.spend}
          share={toNumber(total.headcount.headcount_fte) > 0 ? '100%' : formatShare(null)}
          isBold
          className={styles.totalRow}
        />
      </Table.Body>
    </Table>
  </div>
);
